import type { ReactNode } from 'react';

import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { Icon } from '#/components/Icon/Icon';
import { Initials } from '#/components/Initials/Initials';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';
import { getRgbaString } from '#/utils/style';

import {
  ImagePickerPreview_Box_1,
  ImagePickerPreview_Box_2,
} from './ImagePickerPreview.styled';

type Props = {
  image: ApiFilestackImageData | null | undefined;
  format: 'logo' | 'banner' | 'avatar' | undefined;
  altText: string | undefined;
  previewCrop: boolean | undefined;
  previewCropToCircle: boolean | undefined;
  previewWidth?: number;
  previewHeight?: number;
  defaultImage: ReactNode | undefined;
  allowSVG: boolean | undefined;
  overlayButton: boolean | undefined;
  dragActive: boolean;
  qaId: string;
};

export function ImagePickerPreview({
  image,
  format,
  altText,
  previewCrop,
  previewCropToCircle,
  previewWidth,
  previewHeight,
  defaultImage,
  allowSVG,
  overlayButton,
  dragActive,
  qaId,
}: Props) {
  const { i18n } = useI18n();

  const isBannerWithOverlay = format === 'banner' && overlayButton;

  if (format === 'avatar') {
    if (image) {
      return (
        <FilestackImage
          qaId={`${qaId}-uploaded-image`}
          width={previewWidth}
          height={previewHeight}
          data={image}
          alt={altText ?? i18n('Placeholder')}
          allowSVG={allowSVG}
          round={previewCropToCircle}
          objectFit="cover"
        />
      );
    }

    return (
      <Initials
        name={altText ?? 'P'}
        size={previewWidth ?? 128}
        round={previewCropToCircle}
        backgroundColorName="elementGrey"
      />
    );
  }

  if (format === 'logo') {
    if (image) {
      return (
        <FilestackImage
          qaId={`${qaId}-uploaded-image`}
          width={previewWidth}
          height={previewHeight}
          data={image}
          alt={altText ?? i18n('Placeholder')}
          allowSVG={allowSVG}
          noCrop
        />
      );
    }

    return <Initials name={altText ?? 'P'} size={previewWidth ?? 128} />;
  }

  if (image) {
    return (
      <ImagePickerPreview_Box_2
        data-is-banner-with-overlay={isBannerWithOverlay ? true : undefined}
        style={{ opacity: dragActive ? 0.5 : undefined }}
      >
        <FilestackImage
          qaId={`${qaId}-uploaded-image`}
          width={previewWidth}
          height={isBannerWithOverlay ? 200 : previewHeight}
          alt={altText ?? i18n('Preview image')}
          noCrop={!previewCrop}
          round={previewCropToCircle}
          data={image}
          expandSize={isBannerWithOverlay}
          objectFit={isBannerWithOverlay ? 'cover' : 'contain'}
          allowSVG={allowSVG}
        />
      </ImagePickerPreview_Box_2>
    );
  }

  return (
    <ImagePickerPreview_Box_1
      data-format-equals-banner-and-not-preview-height={
        format === 'banner' && !previewHeight ? true : undefined
      }
      style={{
        height: previewHeight,
        borderRadius: !isBannerWithOverlay ? '8px' : undefined,
        background:
          dragActive && isBannerWithOverlay
            ? `rgba(${getRgbaString(colors.skyBlue, 0.2)})`
            : colors.inputGrey,
        width: format === 'banner' ? '100%' : previewWidth,
      }}
    >
      {!overlayButton &&
        (defaultImage || (
          <div
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Icon name="mountain" size={45} colorName="selectionGrey" />
          </div>
        ))}
    </ImagePickerPreview_Box_1>
  );
}
